import React, { useState } from 'react';
import { 
  User, Mail, Phone, MapPin, CreditCard, Bell, Shield, LogOut, 
  Edit2, ChevronRight, ChevronLeft, Plus, Trash2, Check, Home,
  Eye, EyeOff, Lock, Save, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const Profile = ({ user, onSignOut, onBack }) => {
  const [activeSection, setActiveSection] = useState('main');
  const [isEditing, setIsEditing] = useState(false);
  
  // Profile data state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '+1 613-555-0123',
  });

  // Addresses state
  const [addresses, setAddresses] = useState([
    { id: '1', label: 'Home', address: '123 Bank Street, Ottawa, ON K1P 5N2', isDefault: true },
    { id: '2', label: 'Work', address: '456 Sparks Street, Ottawa, ON K1R 5B3', isDefault: false },
  ]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [newAddress, setNewAddress] = useState({ label: '', address: '' });

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([
    { id: '1', type: 'visa', last4: '4242', expiry: '12/25', isDefault: true },
    { id: '2', type: 'mastercard', last4: '8888', expiry: '06/26', isDefault: false },
  ]);

  // Notifications state
  const [notifications, setNotifications] = useState({
    orderUpdates: true,
    promotions: true,
    newsletter: false,
    pushNotifications: true,
    smsNotifications: false,
  });

  // Security state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Please sign in to view your profile</h2>
        </div>
      </div>
    );
  }

  const menuItems = [
    { id: 'personal', icon: User, label: 'Personal Information', description: 'Name, email, phone' },
    { id: 'addresses', icon: MapPin, label: 'Delivery Addresses', description: 'Manage your addresses' },
    { id: 'payments', icon: CreditCard, label: 'Payment Methods', description: 'Cards and wallets' },
    { id: 'notifications', icon: Bell, label: 'Notifications', description: 'Email and push settings' },
    { id: 'security', icon: Shield, label: 'Privacy & Security', description: 'Password and 2FA' },
  ];

  const handleSaveProfile = () => {
    toast.success('Profile updated successfully');
    setIsEditing(false);
  };

  const handleSetDefaultAddress = (id) => {
    setAddresses(addresses.map(a => ({ ...a, isDefault: a.id === id })));
    toast.success('Default address updated');
  };

  const handleDeleteAddress = (id) => {
    setAddresses(addresses.filter(a => a.id !== id));
    toast.success('Address deleted');
  };

  const handleAddAddress = () => {
    if (!newAddress.label || !newAddress.address) {
      toast.error('Please fill in all fields');
      return;
    }
    setAddresses([...addresses, { 
      id: (addresses.length + 1).toString(), 
      ...newAddress, 
      isDefault: addresses.length === 0 
    }]);
    setNewAddress({ label: '', address: '' });
    toast.success('Address added successfully');
  };

  const handleSetDefaultPayment = (id) => {
    setPaymentMethods(paymentMethods.map(p => ({ ...p, isDefault: p.id === id })));
    toast.success('Default payment method updated');
  };

  const handleDeletePayment = (id) => {
    setPaymentMethods(paymentMethods.filter(p => p.id !== id));
    toast.success('Payment method removed');
  };

  const handleChangePassword = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    toast.success('Password changed successfully');
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'personal':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <Button variant="ghost" onClick={() => setActiveSection('main')} className="p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-2xl font-bold text-gray-800">Personal Information</h2>
            </div>

            <Card className="p-6">
              {/* Profile Photo */}
              <div className="flex items-center gap-6 mb-8 pb-6 border-b">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-3xl font-bold">
                    {profileData.name.charAt(0).toUpperCase()}
                  </div>
                  <button className="absolute bottom-0 right-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white shadow-lg">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{profileData.name}</h3>
                  <p className="text-gray-500">{profileData.email}</p>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    disabled={!isEditing}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    disabled={!isEditing}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    disabled={!isEditing}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                {isEditing ? (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleSaveProfile} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white">
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setIsEditing(true)} className="bg-blue-500 hover:bg-blue-600 text-white">
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
            </Card>
          </div>
        );

      case 'addresses':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <Button variant="ghost" onClick={() => setActiveSection('main')} className="p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-2xl font-bold text-gray-800">Delivery Addresses</h2>
            </div>

            {/* Existing Addresses */}
            <div className="space-y-4">
              {addresses.map((addr) => (
                <Card key={addr.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        addr.label === 'Home' ? 'bg-blue-100' : 'bg-purple-100'
                      }`}>
                        {addr.label === 'Home' ? (
                          <Home className="w-5 h-5 text-blue-600" />
                        ) : (
                          <MapPin className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-800">{addr.label}</h3>
                          {addr.isDefault && (
                            <Badge className="bg-green-100 text-green-700">Default</Badge>
                          )}
                        </div>
                        <p className="text-gray-600 mt-1">{addr.address}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!addr.isDefault && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSetDefaultAddress(addr.id)}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteAddress(addr.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Add New Address */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Add New Address</h3>
              <div className="space-y-4">
                <div>
                  <Label>Label (e.g., Home, Work)</Label>
                  <Input
                    value={newAddress.label}
                    onChange={(e) => setNewAddress({...newAddress, label: e.target.value})}
                    placeholder="Home"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Full Address</Label>
                  <Input
                    value={newAddress.address}
                    onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
                    placeholder="123 Main St, Ottawa, ON K1A 0A1"
                    className="mt-1"
                  />
                </div>
                <Button onClick={handleAddAddress} className="bg-blue-500 hover:bg-blue-600 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Address
                </Button>
              </div>
            </Card>
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <Button variant="ghost" onClick={() => setActiveSection('main')} className="p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-2xl font-bold text-gray-800">Payment Methods</h2>
            </div>

            {/* Existing Payment Methods */}
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <Card key={method.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-8 rounded flex items-center justify-center ${
                        method.type === 'visa' ? 'bg-blue-600' : 'bg-orange-500'
                      } text-white text-xs font-bold`}>
                        {method.type.toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-800">•••• {method.last4}</p>
                          {method.isDefault && (
                            <Badge className="bg-green-100 text-green-700">Default</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">Expires {method.expiry}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!method.isDefault && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSetDefaultPayment(method.id)}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeletePayment(method.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Add New Card */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Add New Card</h3>
              <div className="space-y-4">
                <div>
                  <Label>Card Number</Label>
                  <Input placeholder="1234 5678 9012 3456" className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Expiry Date</Label>
                    <Input placeholder="MM/YY" className="mt-1" />
                  </div>
                  <div>
                    <Label>CVC</Label>
                    <Input placeholder="123" className="mt-1" />
                  </div>
                </div>
                <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Card
                </Button>
              </div>
            </Card>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <Button variant="ghost" onClick={() => setActiveSection('main')} className="p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-2xl font-bold text-gray-800">Notifications</h2>
            </div>

            <Card className="p-6">
              <div className="space-y-6">
                {[
                  { key: 'orderUpdates', label: 'Order Updates', desc: 'Get notified about your order status' },
                  { key: 'promotions', label: 'Promotions & Deals', desc: 'Receive special offers and discounts' },
                  { key: 'newsletter', label: 'Newsletter', desc: 'Weekly updates and new products' },
                  { key: 'pushNotifications', label: 'Push Notifications', desc: 'Browser notifications' },
                  { key: 'smsNotifications', label: 'SMS Notifications', desc: 'Text message alerts' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="font-medium text-gray-800">{item.label}</p>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                    <Switch
                      checked={notifications[item.key]}
                      onCheckedChange={() => {
                        setNotifications({...notifications, [item.key]: !notifications[item.key]});
                        toast.success('Notification preference updated');
                      }}
                    />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <Button variant="ghost" onClick={() => setActiveSection('main')} className="p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-2xl font-bold text-gray-800">Privacy & Security</h2>
            </div>

            {/* Change Password */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-500" />
                Change Password
              </h3>
              <div className="space-y-4">
                <div>
                  <Label>Current Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>New Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    placeholder="Confirm new password"
                    className="mt-1"
                  />
                </div>
                <Button onClick={handleChangePassword} className="bg-blue-500 hover:bg-blue-600 text-white">
                  Update Password
                </Button>
              </div>
            </Card>

            {/* Two-Factor Authentication */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-500" />
                    Two-Factor Authentication
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Add an extra layer of security to your account</p>
                </div>
                <Button variant="outline">Enable</Button>
              </div>
            </Card>

            {/* Delete Account */}
            <Card className="p-6 border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-red-600">Delete Account</h3>
                  <p className="text-sm text-gray-500 mt-1">Permanently delete your account and all data</p>
                </div>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                  Delete Account
                </Button>
              </div>
            </Card>
          </div>
        );

      default:
        return (
          <>
            {/* Back Button */}
            {onBack && (
              <Button 
                variant="ghost" 
                onClick={onBack}
                className="mb-4 flex items-center gap-1 text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="w-5 h-5" />
                Back to Shop
              </Button>
            )}
            
            {/* Profile Header */}
            <Card className="p-6 mb-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-800">{user.name}</h1>
                    {user.isAdmin && <Badge className="bg-red-500 text-white">Admin</Badge>}
                  </div>
                  <p className="text-gray-500">{user.email}</p>
                  <p className="text-gray-500">{user.phone}</p>
                </div>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={() => setActiveSection('personal')}
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Profile
                </Button>
              </div>
            </Card>

            {/* Menu Items */}
            <Card className="divide-y divide-gray-100">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{item.label}</p>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </Card>

            {/* Sign Out Button */}
            <Button
              onClick={onSignOut}
              variant="outline"
              className="w-full mt-6 text-red-500 border-red-200 hover:bg-red-50 flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </>
        );
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {renderSection()}
    </div>
  );
};

export default Profile;
